/**
 * 展立改键 UI：系统/快捷键 → keys.json；键盘单键 → keyboardKeys（type=128, code1=0）
 */
import keysJson from "./config/keys.json";
import { MouseKeyFunction } from "../commonApi";
import {
  normalizeZhanliStoreKeyValue,
  resolveZhanliMacroDisplayName,
} from "./keymap";
import { formatZhanliComboLabel } from "./comboKey";
import {
  ZHANLI_KEYBOARD_TAB_KEYS,
  zhanliKeyboardHidFromEventCode,
  zhanliKeyboardKeyToStoreValue,
  type ZhanliKeyboardKeyGroup,
} from "./keyboardKeys";
import type {
  BeiyingKeymapGroupForUI,
  BeiyingKeymapOptionForUI,
} from "../beiying/keymap";

export type ZhanliKeymapTab = "system" | "keyboard" | "macro";

type KeycodeRow = {
  keyName: string;
  lang?: string;
  type: number;
  code1: number;
  code2: number;
  code3: number;
};

type SystemSection = {
  type: string;
  lang?: string;
  keycodes: KeycodeRow[];
};

type KeysRoot = {
  system: { list: SystemSection[] };
  shortcut: { keycodes: KeycodeRow[] };
};

const DATA = keysJson as KeysRoot;

const ZHANLI_T_HID = 32;
const ZHANLI_T_MM = 33;
const ZHANLI_T_DPI = 80;
const ZHANLI_T_MM48 = 48;

function zhanliHidCode1ToParamHex(code1: number): string {
  const n = code1 & 0xff;
  const map: Record<number, number> = {
    1: 0x0100,
    2: 0x0200,
    4: 0x0400,
    8: 0x0800,
    16: 0x1000,
  };
  const p = map[n] ?? n << 8;
  return `0x${p.toString(16).padStart(4, "0")}`;
}

/** keys.json 一行 → 子应用 store value（与 zhanliMouseKeyToStoreValue 对齐） */
export function zhanliKeycodeRowToUnifiedValue(
  kc: KeycodeRow
): (string | number)[] {
  const t = kc.type;
  const c1 = kc.code1;
  const c2 = kc.code2;
  const c3 = kc.code3;

  if (t === ZHANLI_T_HID) {
    if ((c1 & 0xff) === 0) {
      return [MouseKeyFunction.Disable, "0x0000"];
    }
    return ["0x01", zhanliHidCode1ToParamHex(c1)];
  }
  // 展立火力键 type=64：统一走特殊 Tab 自定义火力
  if (t === 64) {
    return [MouseKeyFunction.FireKey, c1, c2];
  }
  if (t === ZHANLI_T_DPI) {
    const code1ToParam: Record<number, number> = {
      1: 0x0100, // DPI循环
      2: 0x0200, // DPI+
      3: 0x0300, // DPI-
    };
    const param = code1ToParam[c1];
    if (param != null) {
      return ["0x02", `0x${param.toString(16).padStart(4, "0")}`];
    }
  }
  if (t === 0x70 || t === 112) {
    return [MouseKeyFunction.ProfileSwitch, "0x0500"];
  }
  if (t === ZHANLI_T_MM || t === ZHANLI_T_MM48) {
    return normalizeZhanliStoreKeyValue(["zhanli", t, c1, c2, c3]);
  }
  return ["zhanli", t, c1, c2, c3];
}

/** 键盘单键 → demo 无修饰组合键格式 zhanli,128,0,HID,0 */
export function zhanliKeyInfoRowToUnifiedValue(
  hid: number
): (string | number)[] {
  return zhanliKeyboardKeyToStoreValue(hid);
}

function toLabelKey(keyName: string): string {
  return keyName
    .trim()
    .replace(/([a-z0-9])([A-Z])/g, "$1_$2")
    .replace(/[\s+/]+/g, "_")
    .replace(/_+/g, "_")
    .toLowerCase();
}

function rowToOption(
  kc: KeycodeRow,
  groupName: string
): BeiyingKeymapOptionForUI {
  const labelKey = kc.lang?.trim() || toLabelKey(kc.keyName || "key");
  return {
    value: zhanliKeycodeRowToUnifiedValue(kc).map(String).join(","),
    labelKey,
    fallbackLabel: kc.keyName?.trim() || labelKey.replace(/_/g, " "),
    nativeName: kc.keyName,
    group: groupName,
  };
}

const SECTION_TITLE: Record<
  string,
  { titleKey: string; fallbackTitle: string; showNativeTitle?: boolean }
> = {
  mouse: {
    titleKey: "mouse.changeKey.mouseButtons",
    fallbackTitle: "鼠标按键",
  },
  profile: {
    titleKey: "mouse.changeKey.onboardProfile",
    fallbackTitle: "板载配置",
  },
  media: {
    titleKey: "mouse.changeKey.multimediaTab",
    fallbackTitle: "多媒体",
    showNativeTitle: true,
  },
};

function buildShortcutSectionGroup(): BeiyingKeymapGroupForUI | null {
  const items = DATA.shortcut.keycodes.map((kc) => rowToOption(kc, "shortcut"));
  if (items.length === 0) return null;
  return {
    tab: "system",
    group: "shortcut",
    titleKey: "mouse.changeKey.editShortcuts",
    fallbackTitle: "编辑快捷键",
    showNativeTitle: true,
    items,
  };
}

function buildSystemTabGroups(): BeiyingKeymapGroupForUI[] {
  const out: BeiyingKeymapGroupForUI[] = [];
  for (const section of DATA.system.list) {
    const meta = SECTION_TITLE[section.type] || {
      titleKey: "mouse.changeKey.systemTab",
      fallbackTitle: section.type,
      showNativeTitle: true,
    };
    const items = section.keycodes.map((kc) => rowToOption(kc, section.type));
    if (items.length === 0) continue;
    out.push({
      tab: "system",
      group: section.type,
      titleKey: meta.titleKey,
      fallbackTitle: meta.fallbackTitle,
      showNativeTitle: meta.showNativeTitle,
      items,
    });
  }
  const shortcutGroup = buildShortcutSectionGroup();
  if (shortcutGroup) out.push(shortcutGroup);
  return out;
}

const KEYBOARD_SUB_TABS: Array<{
  group: ZhanliKeyboardKeyGroup;
  titleKey: string;
  fallbackTitle: string;
}> = [
  {
    group: "letter",
    titleKey: "mouse.changeKey.letterKeys",
    fallbackTitle: "字母按键",
  },
  {
    group: "number",
    titleKey: "mouse.changeKey.numberKeys",
    fallbackTitle: "数字按键",
  },
  {
    group: "function",
    titleKey: "mouse.changeKey.functionKeys",
    fallbackTitle: "F区功能按键",
  },
  {
    group: "control",
    titleKey: "mouse.changeKey.controlKeys",
    fallbackTitle: "控制键",
  },
  {
    group: "arrow",
    titleKey: "mouse.changeKey.arrowKeys",
    fallbackTitle: "方向键",
  },
];

function keyboardDefToOption(
  def: (typeof ZHANLI_KEYBOARD_TAB_KEYS)[number]
): BeiyingKeymapOptionForUI | null {
  const hid = zhanliKeyboardHidFromEventCode(def.eventCode);
  if (hid == null) return null;
  const labelKey = toLabelKey(def.label);
  return {
    value: zhanliKeyboardKeyToStoreValue(hid).map(String).join(","),
    labelKey,
    fallbackLabel: def.label,
    nativeName: def.label,
    group: "keyboard",
  };
}

function buildKeyboardTabGroups(): BeiyingKeymapGroupForUI[] {
  const buckets: Record<ZhanliKeyboardKeyGroup, BeiyingKeymapOptionForUI[]> = {
    letter: [],
    number: [],
    function: [],
    control: [],
    arrow: [],
  };
  for (const def of ZHANLI_KEYBOARD_TAB_KEYS) {
    const opt = keyboardDefToOption(def);
    if (opt) buckets[def.group].push(opt);
  }
  return KEYBOARD_SUB_TABS.map((cfg) => ({
    tab: "keyboard" as const,
    group: cfg.group,
    titleKey: cfg.titleKey,
    fallbackTitle: cfg.fallbackTitle,
    items: buckets[cfg.group] || [],
  })).filter((g) => g.items.length > 0);
}

/** 展立改键分组（系统/快捷键 → keys.json；键盘 → keyboardKeys） */
export function getZhanliKeymapGroupsForTab(
  tab: Exclude<ZhanliKeymapTab, "macro">
): BeiyingKeymapGroupForUI[] {
  if (tab === "keyboard") return buildKeyboardTabGroups();
  return buildSystemTabGroups();
}

let flatCache: BeiyingKeymapOptionForUI[] | null = null;

function buildFlat(): BeiyingKeymapOptionForUI[] {
  const sys = buildSystemTabGroups().flatMap((g) => g.items);
  const kb = buildKeyboardTabGroups().flatMap((g) => g.items);
  return [...sys, ...kb];
}

export function getZhanliKeyInfosFlat(): BeiyingKeymapOptionForUI[] {
  if (!flatCache) flatCache = buildFlat();
  return flatCache;
}

/** 根据 store value 反查 keys.json 条目 */
export function getZhanliKeymapOptionByValue(
  value: string
): BeiyingKeymapOptionForUI | undefined {
  const flat = getZhanliKeyInfosFlat();
  const lookup = (raw: string) =>
    flat.find((o) => o.value.replace(/\s/g, "") === raw.replace(/\s/g, ""));

  const direct = lookup(value);
  if (direct) return direct;

  const parts = value.split(",").map((s) => s.trim());
  if (!parts.length) return undefined;

  const asArray = parts.map((p) => {
    if (/^0x/i.test(p)) return p;
    const n = Number(p);
    return Number.isFinite(n) && String(n) === p ? n : p;
  }) as (string | number)[];

  const normalized = normalizeZhanliStoreKeyValue(asArray);
  const fromNorm = lookup(normalized.map(String).join(","));
  if (fromNorm) return fromNorm;

  if (String(parts[0]).toLowerCase() === "zhanli" && parts.length === 5) {
    const t = Number(parts[1]);
    const c1 = Number(parts[2]);
    const c2 = Number(parts[3]);
    const c3 = Number(parts[4]);
    if (![t, c1, c2, c3].every((n) => Number.isFinite(n))) return undefined;
    const migrated = zhanliKeycodeRowToUnifiedValue({
      keyName: "",
      type: t,
      code1: c1,
      code2: c2,
      code3: c3,
    })
      .map(String)
      .join(",");
    return lookup(migrated);
  }

  return undefined;
}

/** keys.json shortcut 预设（type 128），用于与自定义组合键区分 */
export function isZhanliShortcutPresetStoreValue(value: string): boolean {
  const norm = value.replace(/\s/g, "");
  return getZhanliKeyInfosFlat().some(
    (item) =>
      item.group === "shortcut" && item.value.replace(/\s/g, "") === norm
  );
}

type ZhanliTranslate = (
  key: string,
  options?: { defaultValue?: string }
) => string;

/** 键盘 Tab 展示文案：与匠盟一致，优先 fallbackLabel */
export function resolveZhanliKeyboardKeyLabel(
  t: ZhanliTranslate,
  opt: Pick<BeiyingKeymapOptionForUI, "labelKey" | "fallbackLabel">
): string {
  if (opt.fallbackLabel === "截图") {
    return t("mouse.changeKey.screenshot");
  }
  const commonKey = `mouse.changeKey.${opt.labelKey}`;
  const common = t(commonKey, { defaultValue: "" });
  if (common && common !== commonKey) return common;
  return opt.fallbackLabel;
}

/** 展立 keys.json lang 字段 → mouse.zhanli.{langId}（与 demo common.json 一致） */
export function resolveZhanliKeyLabel(
  t: ZhanliTranslate,
  opt: Pick<BeiyingKeymapOptionForUI, "labelKey" | "fallbackLabel">
): string {
  if (opt.labelKey) {
    const zhanliKey = `mouse.zhanli.${opt.labelKey}`;
    const localized = t(zhanliKey, { defaultValue: "" });
    if (localized && localized !== zhanliKey) return localized;
  }
  const commonKey = `mouse.changeKey.${opt.labelKey}`;
  const common = t(commonKey, { defaultValue: "" });
  if (common && common !== commonKey) return common;
  return opt.fallbackLabel.replace(/_/g, " ");
}

function parseStoreKeyType(raw: unknown): number | null {
  if (raw == null) return null;
  if (typeof raw === "number" && Number.isFinite(raw)) return raw;
  const s = String(raw).trim();
  if (!s) return null;
  if (/^0x/i.test(s)) return parseInt(s.replace(/^0x/i, ""), 16);
  const n = parseInt(s, 10);
  return Number.isFinite(n) ? n : null;
}

function formatZhanliFireKeyLabel(
  t: ZhanliTranslate,
  interval: number,
  count: number
): string {
  return `${t("mouse.changeKey.fireKey")}(${t("mouse.changeKey.interval")}:${interval}ms, ${t("mouse.changeKey.count")}:${count})`;
}

/**
 * 展立 store 键值 → 鼠标示意图 / 按键列表展示文案
 * 覆盖 keys.json 预设、火力键、组合键及未命中时的兜底
 */
export function formatZhanliStoreKeyLabel(
  t: ZhanliTranslate,
  keyValue: (string | number)[]
): string | undefined {
  if (!keyValue?.length) return undefined;

  const normalized = normalizeZhanliStoreKeyValue(keyValue);
  const joined = normalized.map(String).join(",");
  const opt = getZhanliKeymapOptionByValue(joined);
  if (opt) {
    if (opt.group === "keyboard") {
      return resolveZhanliKeyboardKeyLabel(t, opt);
    }
    return resolveZhanliKeyLabel(t, opt);
  }

  const typeNum = parseStoreKeyType(normalized[0]);
  if (typeNum === MouseKeyFunction.FireKey) {
    const interval = Number(normalized[1]) || 10;
    const count = normalized[2] !== undefined ? Number(normalized[2]) : 1;
    return formatZhanliFireKeyLabel(t, interval, count);
  }
  if (typeNum === MouseKeyFunction.ProfileSwitch) {
    return t("mouse.changeKey.profileCycle");
  }

  if (
    String(normalized[0]).toLowerCase() !== "zhanli" ||
    normalized.length < 5
  ) {
    return undefined;
  }

  const n1 = Number(normalized[1]);
  const n2 = Number(normalized[2]);
  const n3 = Number(normalized[3]);
  const n4 = Number(normalized[4]);
  if (![n1, n2, n3, n4].every((n) => Number.isFinite(n))) return undefined;

  if (n1 === 255 && n2 === 255 && n3 === 255 && n4 === 255) {
    return t("mouse.changeKey.notSet");
  }

  // 滚轮上/下（normalize 后为 zhanli,33,56,1|255,0；keys.json 缺条目时也要能回显）
  if (
    (n1 === 0x21 || n1 === 33 || n1 === 0x30 || n1 === 48) &&
    n2 === 56 &&
    (n3 === 1 || n3 === 255)
  ) {
    return n3 === 1
      ? t("mouse.changeKey.scrollUp")
      : t("mouse.changeKey.scrollDown");
  }

  // type=128：自定义组合键（含无修饰键盘单键；预设已在上方命中）
  if (n1 === 128) {
    const label = formatZhanliComboLabel(n2, n3 > 0 ? n3 : n4);
    return label || t("mouse.changeKey.notSet");
  }

  // type=64：按「特殊」Tab 自定义火力 interval+count 解析
  if (n1 === 64) {
    return formatZhanliFireKeyLabel(t, n2, n3);
  }

  // demo keys.json HID：zhanli,32,code1,0,0（左/右/中/后退/前进）
  if ((n1 === ZHANLI_T_HID || n1 === 32) && n3 === 0 && n4 === 0) {
    const migrated = zhanliKeycodeRowToUnifiedValue({
      keyName: "",
      type: n1,
      code1: n2,
      code2: n3,
      code3: n4,
    })
      .map(String)
      .join(",");
    const hidOpt = getZhanliKeymapOptionByValue(migrated);
    if (hidOpt) return resolveZhanliKeyLabel(t, hidOpt);
  }

  return undefined;
}

function resolveZhanliMouseKeyParamLabel(
  t: ZhanliTranslate,
  typeNum: number,
  paramKey: string
): string | undefined {
  if (typeNum === MouseKeyFunction.MouseKey) {
    const mouseKeyMap: Record<string, string> = {
      "0x0001": t("mouse.changeKey.leftButton"),
      "0x0002": t("mouse.changeKey.rightButton"),
      "0x0004": t("mouse.changeKey.middleButton"),
      "0x0008": t("mouse.changeKey.backward"),
      "0x0010": t("mouse.changeKey.forward"),
      "0x0100": t("mouse.changeKey.leftButton"),
      "0x0200": t("mouse.changeKey.rightButton"),
      "0x0400": t("mouse.changeKey.middleButton"),
      "0x0800": t("mouse.changeKey.backward"),
      "0x1000": t("mouse.changeKey.forward"),
    };
    return mouseKeyMap[paramKey.toLowerCase()];
  }
  if (typeNum === MouseKeyFunction.DPISwitch) {
    const dpiMap: Record<string, string> = {
      "0x0100": t("mouse.changeKey.dpiCycle"),
      "0x0200": t("mouse.changeKey.dpiIncrease"),
      "0x0300": t("mouse.changeKey.dpiDecrease"),
    };
    return dpiMap[paramKey.toLowerCase()] ?? t("mouse.changeKey.dpiSwitch");
  }
  if (typeNum === MouseKeyFunction.Disable) {
    return t("mouse.changeKey.disabled");
  }
  return undefined;
}

/**
 * 展立改键 value → 展示文案（对齐匠盟 resolveJiangmengMouseKeyLabel + demo keys.json）
 */
export function resolveZhanliMouseKeyLabel(
  t: ZhanliTranslate,
  keyValue: (string | number)[],
  options?: { macroNameResolver?: (slot: number) => string | null | undefined }
): string {
  if (!keyValue?.length) return t("mouse.changeKey.notSet");

  const normalized = normalizeZhanliStoreKeyValue(keyValue);
  const typeNum = parseStoreKeyType(normalized[0]);

  const macroLabel = resolveZhanliMacroDisplayName(normalized, options);
  if (macroLabel) return macroLabel;

  const formatted = formatZhanliStoreKeyLabel(t, normalized);
  if (formatted) return formatted;

  if (typeNum != null && normalized.length >= 2) {
    const paramRaw = normalized[1];
    const paramKey =
      typeof paramRaw === "string"
        ? paramRaw.toLowerCase()
        : `0x${(Number(paramRaw) >>> 0).toString(16).padStart(4, "0")}`;
    const mapped = resolveZhanliMouseKeyParamLabel(t, typeNum, paramKey);
    if (mapped) return mapped;
  }

  return t("mouse.changeKey.notSet");
}
